import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Obter o token do serviço de autenticação
  const token = authService.getToken();

  // Adicionar o token ao header se existir. Simulação PIX: usar POST /api/subscriptions/simulate-pix (Bearer); 401 no webhook = back exige secret ou Bearer (ver FRONT_SPEC_TRANSFEERA_PIX.md §7).
  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Interceptar a resposta para tratar erros de autenticação
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Se receber 402 (Payment Required) - assinatura bloqueada
      // Verificar se não é um endpoint público (autenticação ou subscription pública)
      const isPublicEndpoint = 
        req.url.includes('/auth/') || // Endpoints de autenticação
        req.url.toLowerCase().includes('/plans') || // Listar planos (GET /api/Plans — spec)
        req.url.includes('/subscriptions/checkout') || // Criar checkout
        req.url.includes('/subscriptions/current') || // Ver status atual
        req.url.includes('/subscriptions/trial') || // Iniciar trial
        req.url.includes('/billing/stripe'); // Fluxos de billing Stripe (tratados no front)
      
      // 402 no envio de mensagem (créditos WhatsApp): tratar no componente (modal "Comprar créditos"), não redirecionar
      const isInboxMessageSend = req.url.includes('/inbox/threads/') && req.url.includes('/messages') && req.method === 'POST';
      const isInsufficientCredits = error.error?.code === 'insufficient_credits' || error.error?.code === 'INSUFFICIENT_CREDITS';
      
      if (error.status === 402 && !isPublicEndpoint && !(isInboxMessageSend && isInsufficientCredits)) {
        // Redirecionar para tela de billing/paywall
        router.navigate(['/settings/billing'], {
          queryParams: {
            reason: error.error?.code || 'SUBSCRIPTION_EXPIRED',
            message: error.error?.message || 'Sua assinatura expirou ou está com pendência. Regularize o pagamento para continuar utilizando o sistema.'
          }
        });
        return throwError(() => error);
      }

      // Se receber 401 (Unauthorized) e não for a requisição de refresh token
      // Webhook Transfeera é chamado sem auth; 401 nele não deve disparar refresh/logout
      if (error.status === 401 && !req.url.includes('/auth/refresh-token')) {
        const refreshToken = authService.getRefreshToken();
        const friendly401 = new HttpErrorResponse({
          error: error.error,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined,
        });
        Object.defineProperty(friendly401, 'message', { value: 'Sessão expirada. Faça login novamente.', writable: true });

        // Se tiver refresh token, tentar renovar
        if (refreshToken) {
          return authService.refreshToken().pipe(
            switchMap(() => {
              // Repetir a requisição original com o novo token
              const newToken = authService.getToken();
              const clonedReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken}`,
                },
              });
              return next(clonedReq);
            }),
            catchError((refreshError) => {
              // Se o refresh falhar, fazer logout e reenviar erro com mensagem amigável
              authService.logout();
              return throwError(() => friendly401);
            })
          );
        } else {
          // Sem refresh token, fazer logout
          authService.logout();
          return throwError(() => friendly401);
        }
      }

      return throwError(() => error);
    })
  );
};
